import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AtivoFilters } from '../components/AtivoFilters';
import { AtivoTable } from '../components/AtivoTable';
import { NovoAtivoDropdown } from '../components/NovoAtivoDropdown';
import { AtivoFormModal } from '../components/AtivoFormModal';
import { LicencaFormModal } from '../components/LicencaFormModal';
import { LicencaDetalheModal } from '../components/LicencaDetalheModal';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { useCatalogoAtivos, useExcluirLicense, useLicense } from '../hooks';
import { authService } from '../../../shared/lib/auth';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import type { ListAssetsParams } from '../api';

const PAGE_SIZE = 25;

/** Filtros que representam "usuário aplicou algo" (offset/limit não contam). */
function hasActiveFilters(f: ListAssetsParams): boolean {
  return Boolean(
    f.status || f.categoryId || f.custodianId || f.locationId ||
    f.costCenterId || f.projectId || f.clientId || f.companyId ||
    (f.search && f.search.trim().length > 0),
  );
}

interface AtivosPageProps {
  /** Quando true, suprime o PageContainer externo (usado como aba em AtivosHomePage). */
  embedded?: boolean;
}

type TypeFilter = 'all' | 'physical' | 'software';

const TYPE_FILTER_LABELS: Record<TypeFilter, string> = {
  all: 'Todos',
  physical: 'Físico',
  software: 'Software / Licença',
};

/** `?tipo=software` vem dos links que antes apontavam para a aba Licenças. */
function typeFilterFrom(value: string | null): TypeFilter {
  return value === 'software' ? 'software' : 'all';
}

export default function AtivosPage({ embedded = false }: AtivosPageProps = {}) {
  // Ativos e licenças na mesma grid (issue #464). `?open=<id>` abre o
  // detalhe da licença — links de alertas e da página do ativo usam.
  const [params, setParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(() => typeFilterFrom(params.get('tipo')));
  const [filters, setFilters] = useState<ListAssetsParams>({ limit: PAGE_SIZE, offset: 0 });
  const [ativoModalOpen, setAtivoModalOpen] = useState(false);
  const [licencaModalOpen, setLicencaModalOpen] = useState(false);
  const [editingLicenseId, setEditingLicenseId] = useState<string | null>(null);
  const [deletingLicenseId, setDeletingLicenseId] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const navigate = useNavigate();
  const query = useCatalogoAtivos({
    ...filters,
    assetType: typeFilter === 'all' ? undefined : typeFilter,
  });
  const user = authService.getCurrentUser();
  const { can } = usePermissions();
  const canCreate = can(FEATURES.ativos, 'create');
  const canEditLicense = can(FEATURES.ativosLicencas, 'edit');
  const canDeleteLicense = can(FEATURES.ativosLicencas, 'delete');

  const openLicenseId = params.get('open');
  // Detalhe carregado sob demanda para preencher o formulário de edição.
  const editingLicense = useLicense(editingLicenseId ?? undefined);
  const deletingLicense = useLicense(deletingLicenseId ?? undefined);
  const excluirLicense = useExcluirLicense();

  function openLicense(id: string) {
    setParams((p) => { p.set('open', id); return p; });
  }
  function closeLicense() {
    setParams((p) => { p.delete('open'); return p; });
  }

  async function handleDeleteLicense() {
    if (!deletingLicenseId) return;
    try {
      await excluirLicense.mutateAsync(deletingLicenseId);
      closeLicense();
      setBanner('Licença excluída.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setBanner(typeof msg === 'string' ? msg : 'Falha ao excluir licença');
    } finally {
      setDeletingLicenseId(null);
    }
  }

  const limit = filters.limit ?? PAGE_SIZE;
  const offset = filters.offset ?? 0;
  const count = query.data?.pagination.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  const canPrev = offset > 0;
  const canNext = offset + limit < count;

  // Ao mudar qualquer filtro, sempre voltar para a página 1 — senão o usuário
  // pode ficar em uma página vazia depois de restringir os filtros.
  function applyFilterChange(patch: Partial<ListAssetsParams>) {
    setFilters((prev) => ({ ...prev, ...patch, offset: 0 }));
  }

  function resetFilters() {
    setFilters({ limit: PAGE_SIZE, offset: 0 });
  }

  const Wrap = ({ children }: { children: ReactNode }) =>
    embedded ? (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Cadastro, movimentação e conformidade patrimonial.</p>
          {canCreate && (
            <NovoAtivoDropdown
              onNewAtivo={() => setAtivoModalOpen(true)}
              onNewLicenca={() => setLicencaModalOpen(true)}
            />
          )}
        </div>
        {children}
      </div>
    ) : (
      <PageContainer
        title="Gestão de Ativos"
        subtitle="Cadastro, movimentação e conformidade patrimonial."
        action={canCreate ? (
          <NovoAtivoDropdown
            onNewAtivo={() => setAtivoModalOpen(true)}
            onNewLicenca={() => setLicencaModalOpen(true)}
          />
        ) : undefined}
      >
        {children}
      </PageContainer>
    );

  return (
    <Wrap>
      <AtivoFilters filters={filters} onChange={applyFilterChange} onReset={resetFilters} />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Tipo:</span>
        {(Object.keys(TYPE_FILTER_LABELS) as TypeFilter[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              setTypeFilter(k);
              setFilters((prev) => ({ ...prev, offset: 0 }));
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              typeFilter === k
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {TYPE_FILTER_LABELS[k]}
          </button>
        ))}
      </div>

      {banner && (
        <Alert
          variant="info"
          action={<Button variant="ghost" size="sm" onClick={() => setBanner(null)}>fechar</Button>}
        >
          {banner}
        </Alert>
      )}

      {query.isError && (
        <Alert variant="error">
          Não foi possível carregar os ativos. Verifique sua conexão e tente novamente.
        </Alert>
      )}

      <AtivoTable
        ativos={query.data?.data ?? []}
        isLoading={query.isLoading}
        hasActiveFilters={hasActiveFilters(filters)}
        onClearFilters={resetFilters}
        onOpenLicense={openLicense}
      />

      {query.data?.pagination && count > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>
            Mostrando {offset + 1}–{Math.min(offset + limit, count)} de {count} ativo(s)
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={!canPrev}
              onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, offset - limit) }))}
              icon={<ChevronLeft className="h-3.5 w-3.5" />}
              aria-label="Página anterior"
            >
              Anterior
            </Button>
            <span className="min-w-[80px] text-center">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={!canNext}
              onClick={() => setFilters((f) => ({ ...f, offset: offset + limit }))}
              aria-label="Próxima página"
            >
              Próxima <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      <AtivoFormModal
        open={ativoModalOpen}
        onClose={() => setAtivoModalOpen(false)}
        onSuccess={(asset) => navigate(`/ativos/${asset.asset.id}`)}
      />
      <LicencaFormModal
        open={licencaModalOpen}
        onClose={() => setLicencaModalOpen(false)}
        onSuccess={(license) => openLicense(license.id)}
      />

      {openLicenseId && !editingLicenseId && (
        <LicencaDetalheModal
          open
          licenseId={openLicenseId}
          canWrite={canEditLicense}
          onClose={closeLicense}
          onEdit={canEditLicense ? () => setEditingLicenseId(openLicenseId) : undefined}
          onDelete={canDeleteLicense ? () => setDeletingLicenseId(openLicenseId) : undefined}
        />
      )}
      <LicencaFormModal
        open={!!editingLicenseId && !!editingLicense.data}
        initial={editingLicense.data?.license}
        onClose={() => setEditingLicenseId(null)}
        onSuccess={() => {
          setEditingLicenseId(null);
          setBanner('Licença atualizada.');
        }}
      />
      <ConfirmDialog
        open={!!deletingLicenseId}
        title="Excluir licença"
        message={`Confirmar exclusão de "${deletingLicense.data?.license.name ?? 'esta licença'}"? Só é possível excluir licenças sem seats ativos.`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        danger
        busy={excluirLicense.isPending}
        onConfirm={handleDeleteLicense}
        onCancel={() => setDeletingLicenseId(null)}
      />
    </Wrap>
  );
}
